import _ from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import { clearApolloCache } from '@/shared/api';
import {
  EMITTER_CONSTANTS,
  AI_COMMAND,
  canAvgRhythmEvents,
  canShowCountBeatEvents,
  canShowDurationEvents,
  KEY_CANCEL,
  STRIP_AFIB_THUMBNAIL_INFO,
  STRIP_ARTIFACT_THUMBNAIL_INFO,
  TYPE_RHYTHM_EVENT_ENUM,
} from '@/shared/config';
import { useEmitter } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import { removeSavingData } from '@/entities/beat';
import { logError } from '@/shared/lib/apiHelpers';
import {
  isAppliedFilterEvent,
  fetchHolterEventIndex,
  fetchHolterEventsDailyCount,
  fetchHolterRhythmEventsCount,
} from '@/entities/event';

import emitter from '@/shared/lib/eventEmitter';

// Apollo Functions
import {
  fetchHolterBeatEventsCount,
  fetchHolterRrHeatMapCount,
  fetchHolterRrHeatMapOverview,
} from '@/entities/beat';
import { formatHolterRrHeatMapCount } from '@/widgets/beats/ui/rrInterval/handler';
import { formatDataChart } from '@/widgets/beats/ui/rrInterval/heatMapChart/handler';
import type { IEvent, IReportInfo } from '@/shared/model';
import {
  selectActiveTab,
  selectEventFilter,
  selectIsActiveTab,
  selectPageIndex,
  selectSelectedDateValue,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import { DATA_HEATMAP_STATUS } from '@/shared/model';
import { generateDefaultHeatmapState } from '@/shared/lib/heatmap';

type BeatDataEffectProps = { keyRecoil: string };

/**
 * Headless effect component that keeps the beats tab's report-store data in sync with AI processing and socket updates. Binds every getter and setter to the given tab key, subscribes to emitter and socket messages for event reloads, minute summaries, RR heatmap and overview refreshes and saving status, queues incoming update messages while saving is in flight, and clears pending local beat, event and template changes once the server confirms them.
 *
 * @param keyRecoil - Report-store record key identifying which tab's state this effect drives.
 * @returns Always null; the component renders no markup and exists only for its subscriptions.
 */
const BeatDataEffect = ({ keyRecoil }: BeatDataEffectProps): null => {
  const { studyId, profileId, startReport, stopReport } = useReportStore(
    (s) => s.reportInfo,
  ) as IReportInfo;

  // use recoil getter
  /**
   * Reads the beats tab's currently active event category. Applies the active-tab selector to a store snapshot so effects and socket handlers see the live value.
   *
   * @returns The active rhythm, beat or heart-rate tab value.
   */
  const getActiveButton = () => selectActiveTab(keyRecoil)(useReportStore.getState());
  /**
   * Reads the strip currently selected in this tab. Applies the selected-strip selector to a store snapshot instead of subscribing.
   *
   * @returns The selected event, beat or ECG bookmark.
   */
  const getSelectedStrip = () => selectSelectedStrip(keyRecoil)(useReportStore.getState());
  /**
   * Reads the day currently selected in this tab. Applies the selected-date selector to a store snapshot instead of subscribing.
   *
   * @returns The selected date as an ISO string.
   */
  const getSelectedDateValue = () => selectSelectedDateValue(keyRecoil)(useReportStore.getState());
  /**
   * Reads this tab's paging state from the report store. Applies the page-index selector to a snapshot instead of subscribing.
   *
   * @returns The tab's page index state.
   */
  const getPageIndex = () => selectPageIndex(keyRecoil)(useReportStore.getState());

  // use recoil setter
  const setReloadEcgViewerAction = useReportStore((s) => s.actions.setReloadEcgViewer);
  /**
   * Bumps the counter that forces the ECG viewer to reload. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setReloadEcgViewer = (v: Parameters<typeof setReloadEcgViewerAction>[1]) =>
    setReloadEcgViewerAction(keyRecoil, v);
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Writes the pending beat changes for this tab. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the pending event changes for this tab. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Writes the aggregated beat option snapshot for this tab. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  const setGroupEventChangesAction = useReportStore((s) => s.actions.setGroupEventChanges);
  /**
   * Writes the grouped event changes for this tab. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setGroupEventChanges = (v: Parameters<typeof setGroupEventChangesAction>[1]) =>
    setGroupEventChangesAction(keyRecoil, v);
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Sets the currently selected strip for this tab. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const setReloadHrChartAction = useReportStore((s) => s.actions.setReloadHrChart);
  /**
   * Bumps the counter that forces the heart-rate chart to reload. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setReloadHrChart = (v: Parameters<typeof setReloadHrChartAction>[1]) =>
    setReloadHrChartAction(keyRecoil, v);
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Writes the list of other-category events. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(keyRecoil, v);
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Writes the list of newly created events. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the aggregated event option snapshot for this tab. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const setDailyCountAction = useReportStore((s) => s.actions.setDailyCount);
  /**
   * Writes the per-day event counts. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setDailyCount = (v: Parameters<typeof setDailyCountAction>[1]) =>
    setDailyCountAction(keyRecoil, v);
  const setArtifactDailyCountAction = useReportStore((s) => s.actions.setDailyArtifactCount);
  /**
   * Writes the per-day artifact strip counts. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setArtifactDailyCount = (v: Parameters<typeof setArtifactDailyCountAction>[1]) =>
    setArtifactDailyCountAction(keyRecoil, v);
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Sets the beats tab's page index. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const setBulkBeatsChangesAction = useReportStore((s) => s.actions.setBulkBeatsChanges);
  /**
   * Writes the pending bulk beat changes. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setBulkBeatsChanges = (v: Parameters<typeof setBulkBeatsChangesAction>[1]) =>
    setBulkBeatsChangesAction(keyRecoil, v);
  const setResetBulkBeatsChangesAction = useReportStore((s) => s.actions.setResetBulkBeatsChanges);
  /**
   * Writes the bulk beat changes queued for reset. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setResetBulkBeatsChanges = (v: Parameters<typeof setResetBulkBeatsChangesAction>[1]) =>
    setResetBulkBeatsChangesAction(keyRecoil, v);
  // use recoil value

  const eventFilter = useReportStore(selectEventFilter(keyRecoil));
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));

  // use recoil getter not keyRecoil
  const setTotalStrip = useReportStore((s) => s.actions.setTotalStrip);
  /**
   * Reads the per-type load status of the RR heatmap charts from the report store. Takes a snapshot rather than subscribing, so reload handlers can decide which charts to refetch.
   *
   * @returns The heatmap chart status map keyed by chart type.
   */
  const getDataHeatmapChartStatus = () => useReportStore.getState().dataHeatmapChartStatus;
  /**
   * Reads the cached ECG minute-summary data map from the report store. Takes a snapshot so socket handlers can patch it without subscribing.
   *
   * @returns The current ECG data map.
   */
  const getEcgDataMap = () => useReportStore.getState().ecgDataMap;
  const setHolterRhythmEventsCount = useReportStore((s) => s.actions.setRhythmEventCount);
  const setHolterBeatEventsCount = useReportStore((s) => s.actions.setBeatEventCount);

  const setDataHeatmapChart = useReportStore((s) => s.actions.setDataHeatmapChart);
  const setDataHeatmapChartStatus = useReportStore((s) => s.actions.setDataHeatmapChartStatus);

  const setEcgDataMap = useReportStore((s) => s.actions.setEcgDataMap);
  const setRrOverviewOrigin = useReportStore((s) => s.actions.setRrOverviewOrigin);

  const setBulkReviewedBeats = useReportStore((s) => s.actions.setBulkReviewedBeats);
  const setResetBulkReviewedBeats = useReportStore((s) => s.actions.setResetBulkReviewedBeats);
  const setReviewedBeatsChanges = useReportStore((s) => s.actions.setReviewedBeatsChanges);

  const setSwapBeatChangesAction = useReportStore((s) => s.actions.setSwapBeatChanges);
  /**
   * Writes the pending beat swap changes. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setSwapBeatChanges = (v: Parameters<typeof setSwapBeatChangesAction>[1]) =>
    setSwapBeatChangesAction(keyRecoil, v);
  const setBulkSwapBeatsAction = useReportStore((s) => s.actions.setBulkSwapBeats);
  /**
   * Writes the pending bulk beat swaps. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setBulkSwapBeats = (v: Parameters<typeof setBulkSwapBeatsAction>[1]) =>
    setBulkSwapBeatsAction(keyRecoil, v);
  const setResetSwapBeatsAction = useReportStore((s) => s.actions.setResetSwapBeats);
  /**
   * Writes the beat swaps queued for reset. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setResetSwapBeats = (v: Parameters<typeof setResetSwapBeatsAction>[1]) =>
    setResetSwapBeatsAction(keyRecoil, v);
  const setTemplateChangesAction = useReportStore((s) => s.actions.setTemplateChanges);
  /**
   * Writes the pending beat template changes. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setTemplateChanges = (v: Parameters<typeof setTemplateChangesAction>[1]) =>
    setTemplateChangesAction(keyRecoil, v);
  const setInitTemplateOverview = useReportStore((s) => s.actions.setInitTemplateOverview);

  const isPushQueue = useReportStore((s) => s.isPushQueue);
  const setIsPushQueue = useReportStore((s) => s.actions.setIsPushQueue);
  const isStartedAiProcessing = useReportStore((s) => s.isStartedAiProcessing);
  const updateDataMessageQueue = useRef([]);

  /**
   * Refreshes the per-type beat and rhythm event totals shown on the beats tab. Requests both holter counts for the study and profile in parallel and stores them, logging and swallowing any failure.
   *
   * @returns A promise resolving once both count sets have been stored.
   */
  const fetchEventsCount = async () => {
    try {
      const eventsCountFilter = {
        studyId,
        profileId,
      };
      const promises = [
        fetchHolterBeatEventsCount(eventsCountFilter, true, null, false),
        fetchHolterRhythmEventsCount(eventsCountFilter, true, null, false),
      ];
      const [holterBeatEventsCount, holterRhythmEventsCount] = await Promise.all(promises);
      setHolterBeatEventsCount(holterBeatEventsCount);
      setHolterRhythmEventsCount(holterRhythmEventsCount);
    } catch (error) {
      logError('Failed to fetch holter rhythm events count: ', error);
    }
  };

  const formatRrOverview = useMemo(
    () => (rrOverview: any[]) => {
      const result: Record<string, any> = generateDefaultHeatmapState({
        reviewed: 1,
        total: 1,
        isEmpty: true,
      });
      _.forEach(rrOverview, (x) => {
        result[x.rrHeatMapType] =
          x.total === 0
            ? {
                reviewed: 1,
                total: 1,
                isEmpty: true,
              }
            : {
                reviewed: x.reviewed,
                total: x.total,
                isEmpty: false,
              };
      });
      return result;
    },
    [],
  );

  /**
   * Refreshes the RR histogram overview for the current study. Fetches the holter RR heatmap overview, formats it and stores it as the overview origin, logging and swallowing any failure.
   *
   * @returns A promise resolving to an empty object once the overview has been stored.
   */
  const handleFetchRrOverview = async () => {
    // check data from indexdb, if true, no need to fetch data from server
    try {
      const rrOverview = await fetchHolterRrHeatMapOverview(
        {
          studyId,
          profileId,
        },
        null,
        true,
        true,
      );

      const formattedRrOverview = formatRrOverview(rrOverview);
      setRrOverviewOrigin(formattedRrOverview as any);
    } catch (error) {
      logError('Failed to fetch rr histogram overview', error);
    }
    return {};
  };

  /**
   * Fetches and formats the RR heatmap counts for one heatmap type of the current study. Queries the holter RR heatmap count endpoint for the study and profile, formats the response, and on failure logs the error, shows a toast and yields an empty result.
   *
   * @param key - RR heatmap type to fetch.
   * @returns A promise resolving to a single-entry map from the heatmap type to its formatted data, or an empty map on failure.
   */
  const fetchDataHeatmapChart = async (key: string) => {
    try {
      const filter = {
        studyId,
        profileId,
        rrHeatMapType: key,
      };
      const result = await fetchHolterRrHeatMapCount(filter, null, true);
      const dataHeatmapChart = formatHolterRrHeatMapCount(result);
      return {
        [key]: dataHeatmapChart,
      };
    } catch (error) {
      logError('Failed to fetch rr heatmap data', error);
      toastrError(error.message);
      return {};
    }
  };

  /**
   * Pushes freshly fetched RR heatmap data into the chart instances so their cells repaint. Iterates the map and calls the chart formatter once per heatmap type with that type's data.
   *
   * @param listDataHeatmapChart - Newly fetched heatmap data keyed by RR heatmap type.
   * @returns A promise resolving once every chart type has been handed its data.
   */
  const updateCellsStatus = async (listDataHeatmapChart: Record<string, any>) => {
    _.forEach(listDataHeatmapChart, (newDataHeatmapChart, key) => {
      // formatDataChart's JSDoc param type (BeatHR/rrInterval/heatMapChart/handler, not yet
      // converted) declares more required fields than this call site ever passed.
      formatDataChart({
        currentDataHeatmapChart: newDataHeatmapChart,
        type: key,
      } as any);
    });
  };

  /**
   * Refetches every RR heatmap chart that already holds data and repaints it. Fetches in parallel only the types whose status is success or empty, merges the results into the chart data and status maps, hands the new data to the chart instances, and emits the heatmap update event on the next tick so the store writes have settled.
   *
   * @returns A promise resolving once the heatmap data and statuses have been stored.
   */
  const handleReloadRrHeatMap = async () => {
    const promiseArr: Promise<any>[] = [];
    _.forEach(getDataHeatmapChartStatus(), (value, key) => {
      if (value === DATA_HEATMAP_STATUS.SUCCESS || value === DATA_HEATMAP_STATUS.EMPTY) {
        promiseArr.push(fetchDataHeatmapChart(key));
      }
    });

    const data = await Promise.all(promiseArr);
    const newDataHeatmapChart: Record<string, any> = {};
    const newDataHeatMapChartStatus: Record<string, any> = {};
    _.forEach(data, (value) => {
      if (!_.isEmpty(value)) {
        _.assign(newDataHeatmapChart, value);
      }
    });

    Object.keys(newDataHeatmapChart).forEach((key) => {
      if (_.isEmpty(newDataHeatmapChart[key])) {
        newDataHeatMapChartStatus[key] = DATA_HEATMAP_STATUS.EMPTY;
      } else {
        newDataHeatMapChartStatus[key] = DATA_HEATMAP_STATUS.SUCCESS;
      }
    });
    setDataHeatmapChart((prev) => ({
      ...prev,
      ...newDataHeatmapChart,
    }));
    setDataHeatmapChartStatus((prev) => ({
      ...prev,
      ...newDataHeatMapChartStatus,
    }));
    // use setTimeOut in order to waiting all setState in recoil
    updateCellsStatus(newDataHeatmapChart);
    setTimeout(() => {
      emitter.emit(EMITTER_CONSTANTS.UPDATE_HEATMAP_CHART_DATA);
    }, 0);
  };

  /**
   * Reloads the artifact or AFib event data after the AI service reports a change. Refetches the daily counts for the active type with the current event filter applied, keeps the selected strip on its page by re-resolving its index, reloads the visible event page and, unless the reload came from an outside command, bumps the ECG viewer and heart-rate chart reload counters.
   *
   * @param msg - Change payload from the AI service, carrying the AFib artifact flag.
   * @param isOutSideCommand - True when the reload was triggered outside this tab, which suppresses the viewer and chart refresh.
   * @returns A promise resolving once counts, events and selection have been refreshed.
   */
  const handleReloadEvent = async (msg: any, isOutSideCommand = false) => {
    try {
      const { isAFibArtifact } = msg;
      const selectedDateValue = getSelectedDateValue();
      const type = getActiveButton();

      let filterDailyCount: any = {};
      if (type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
        filterDailyCount = {
          studyId,
          profileId,
          types: [type],
          startSearchDate: startReport,
          stopSearchDate: stopReport,
        };
      } else if (type === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
        const additionalFilter = {};
        if (isAppliedFilterEvent(eventFilter, type)) {
          _.assign(additionalFilter, {
            ...(eventFilter.isHideReviewed && { isReviewed: false }),
            ...(eventFilter.isCapture && { isCaptured: true }),
            ...(eventFilter.sortOrder &&
              (canShowCountBeatEvents.includes(type) ||
                canShowDurationEvents.includes(type) ||
                canAvgRhythmEvents.includes(type)) && {
                sortBy: eventFilter.sortBy || 'countBeats',
                sortOrder: eventFilter.sortOrder,
              }),
          });
        }

        filterDailyCount = {
          studyId,
          profileId,
          startSearchDate: startReport,
          stopSearchDate: stopReport,
          types: [TYPE_RHYTHM_EVENT_ENUM.AFIB],
          ...additionalFilter,
        };
      }
      let dailyCount: any[] = [];
      if (type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT || type === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
        dailyCount = await fetchHolterEventsDailyCount(filterDailyCount);
      }

      if (!_.isEmpty(dailyCount)) {
        const currentSelectedDateValue = getSelectedDateValue();
        const foundDailyCount = _.find(
          dailyCount,
          (x) => x.date === currentSelectedDateValue,
        )?.count;
        if (getActiveButton() === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
          setTotalStrip(foundDailyCount ?? 0);
          setArtifactDailyCount(dailyCount);
        } else {
          setDailyCount(dailyCount);
        }
      }

      const selectedStrip = getSelectedStrip();
      // selectedStripState is IEvent | IBeat | IEcgBookmark | null; idEvent only exists on IEvent.
      if ((selectedStrip as IEvent)?.idEvent) {
        let filterHolterEventIndex = {};
        if (type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
          filterHolterEventIndex = {
            studyId,
            profileId,
            types: [type],
            includedEventId: (selectedStrip as IEvent).idEvent,
            startRange: {
              start: selectedDateValue,
              stop: moment(selectedDateValue).add(1, 'days').toISOString(),
            },
          };
        } else if (type === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
          const additionalFilter = {};
          if (isAppliedFilterEvent(eventFilter, type)) {
            _.assign(additionalFilter, {
              ...(eventFilter.isHideReviewed && { isReviewed: false }),
              ...(eventFilter.isCapture && { isCaptured: true }),
              ...(eventFilter.sortOrder &&
                (canShowCountBeatEvents.includes(type) ||
                  canShowDurationEvents.includes(type) ||
                  canAvgRhythmEvents.includes(type)) && {
                  sortBy: eventFilter.sortBy || 'countBeats',
                  sortOrder: eventFilter.sortOrder,
                }),
            });
          }

          filterHolterEventIndex = {
            studyId,
            profileId,
            types: [type],
            includedEventId: (selectedStrip as IEvent).idEvent,
            ...additionalFilter,
          };
        }

        const eventIndex = await fetchHolterEventIndex(
          filterHolterEventIndex,
          false,
          KEY_CANCEL.API_HOLTER_AI,
        );
        if (!_.isNil(eventIndex) && eventIndex >= 0) {
          let page = 0;
          if (type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
            page = Math.floor(eventIndex / STRIP_ARTIFACT_THUMBNAIL_INFO.stripDisplayLimit);
          } else if (type === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
            page = Math.floor(eventIndex / STRIP_AFIB_THUMBNAIL_INFO.stripDisplayLimit);
          } else {
            page = getPageIndex().index;
          }
          setPageIndex({ index: page < 0 ? 0 : page });
        }
      }
      if (isOutSideCommand && msg.isRecapture && msg.recaptureEventsAfterUpdating && isActiveTab) {
        await clearApolloCache();
        setReloadHrChart((prev) => prev + _.round(Math.random() * 100));
        setReloadEcgViewer((prev) => prev + _.round(Math.random() * 100));
        if (!msg.isRecapture) {
          emitter.emit(EMITTER_CONSTANTS.RE_RENDER_ECG_VIEWER);
        }
      } else if (isAFibArtifact) {
        setReloadHrChart((prev) => prev + _.round(Math.random() * 100));
        setReloadEcgViewer((prev) => prev + _.round(Math.random() * 100));
      }
    } catch (error) {
      console.error('Error handling reload event:', error);
    }
  };

  /**
   * Applies incoming minute-summary updates to the cached ECG data. Copies the current ECG data map, patches the average, maximum and minimum heart rates of every summary whose id is already loaded, writes the copy back only when it differs, and asks the heart-rate chart to re-render.
   *
   * @param msg - Socket payload carrying the updated minute summaries, either directly or nested under the beats update.
   * @returns A promise resolving once the ECG data map has been patched and the chart notified.
   */
  const handleMinutlySummary = async (msg: any) => {
    const prevEcgDataMap = getEcgDataMap();
    const cloneEcgDataMap = { ...prevEcgDataMap };
    if (
      (msg.summaries?.length || msg.beatsUpdated?.summaries?.length) &&
      !_.isEmpty(prevEcgDataMap?.data)
    ) {
      _.forEach(msg.summaries || msg.beatsUpdated?.summaries, (summary) => {
        const foundData = _.find(cloneEcgDataMap.data, (x) => x.id === summary.id);
        if (foundData) {
          _.assign(foundData, {
            avgHrs: summary?.avgHrs,
            maxHrs: summary?.maxHrs,
            minHrs: summary?.minHrs,
          });
        }
      });
    }
    setEcgDataMap((prev: any) => {
      if (_.isEqual(prev, cloneEcgDataMap)) {
        return prev;
      }
      return cloneEcgDataMap;
    });
    emitter.emit(EMITTER_CONSTANTS.RE_RENDER_HR_CHART);
  };

  /**
   * Drops the locally pending beat, event and template edits that the server has now persisted. Filters the saving entries out of every beat, bulk beat, reviewed, swap and template change list plus the event change, new and other lists, mirrors the surviving lists into the aggregated beat and event option objects, and clears the grouped event changes.
   *
   * @returns Nothing.
   */
  const removeChangeData = () => {
    // resetEvent beatChange
    const resetBulkBeatsChanges: any[] = [];
    let bulkBeatsChanges: any[] = [];
    let beatsChanges: any[] = [];
    let reviewBeatChanges: any[] = [];
    let bulkReviewedBeats: any[] = [];
    let resetReviewChanges: any[] = [];
    let swapBeatChanges: any[] = [];
    let bulkSwapBeats: any[] = [];
    let resetSwapBeats: any[] = [];
    let templateChanges: any[] = [];

    setBulkBeatsChanges((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      bulkBeatsChanges = removeSavingData(prev);
      return bulkBeatsChanges;
    });
    setResetBulkBeatsChanges((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      resetBulkBeatsChanges.push(...removeSavingData(prev));
      return resetBulkBeatsChanges;
    });
    setBeatChanges((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      const beats = removeSavingData(prev);
      beatsChanges = beats;
      return beats;
    });
    setReviewedBeatsChanges((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      reviewBeatChanges = removeSavingData(prev);
      return reviewBeatChanges;
    });
    setBulkReviewedBeats((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      bulkReviewedBeats = removeSavingData(prev);
      return bulkReviewedBeats;
    });
    setResetBulkReviewedBeats((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      resetReviewChanges = removeSavingData(prev);
      return resetReviewChanges;
    });
    setSwapBeatChanges((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      swapBeatChanges = removeSavingData(prev);
      return swapBeatChanges;
    });
    setBulkSwapBeats((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      bulkSwapBeats = removeSavingData(prev);
      return bulkSwapBeats;
    });
    setResetSwapBeats((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      resetSwapBeats = removeSavingData(prev);
      return resetSwapBeats;
    });
    setTemplateChanges((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      templateChanges = removeSavingData(prev);
      return templateChanges;
    });
    setBeatOptions((prev: any) => ({
      ...prev,
      bulkBeatsChangesState: bulkBeatsChanges,
      resetBulkBeatsChangesState: resetBulkBeatsChanges,
      beatChangesState: beatsChanges,
      reviewedBeatsChangesState: reviewBeatChanges,
      bulkReviewedBeatsState: bulkReviewedBeats,
      resetBulkReviewedBeatsState: resetReviewChanges,
      swapBeatChangesState: swapBeatChanges,
      bulkSwapBeatsState: bulkSwapBeats,
      resetSwapBeatsState: resetSwapBeats,
    }));

    // resetEvent Change
    let eventChanges: any[] = [];
    let eventNews: any[] = [];
    let eventOthers: any[] = [];
    setGroupEventChanges((prev: any) => {
      if (prev.length === 0) {
        return prev;
      }
      return [];
    });
    setEventChanges((prev: any) => {
      let count = 0;
      const filterResult = prev.filter((x: any) => {
        if (x.isSaving) {
          count += 1;
          return false;
        }
        return true;
      });
      if (count === 0) {
        return prev;
      }
      eventChanges.push(...filterResult);
      return filterResult;
    });
    setEventOthers((prev: any) => {
      let count = 0;
      const filterResult = prev.filter((x: any) => {
        if (x.isSaving) {
          count += 1;
          return false;
        }
        return true;
      });
      if (count === 0) {
        return prev;
      }
      eventOthers.push(...filterResult);
      return filterResult;
    });
    setEventNews((prev: any) => {
      let count = 0;
      const filterResult = prev.filter((x: any) => {
        if (x.isSaving) {
          count += 1;
          return false;
        }
        return true;
      });
      if (count === 0) {
        return prev;
      }
      eventNews.push(...filterResult);
      return filterResult;
    });
    setEventOptions({
      eventNewsState: eventNews,
      eventChangesState: eventChanges,
      eventOthersState: eventOthers,
    });
    const selectedStrip = getSelectedStrip();
    if ((selectedStrip as IEvent)?.isNew) {
      setSelectedStrip(null);
    }
    eventChanges = [];
    eventNews = [];
    eventOthers = [];
  };

  // TODO SAVE SOCKET
  /**
   * Drains the queued AI update messages once the backend reports that saving finished. Clears the Apollo cache, then runs the queued event reloads with their count refresh and minute-summary patches together with the RR overview and heatmap reloads, drops the confirmed local changes, and finally empties the queue, hides the AI loading indicator and asks the ECG viewer and template overview to re-render.
   *
   * @returns A promise resolving once every queued reload has settled and the queue has been emptied.
   */
  const handleSavingStatus = async () => {
    try {
      await clearApolloCache();
      const promises = [];
      updateDataMessageQueue.current.forEach((x) => {
        if (x.type === AI_COMMAND.EVENT) {
          promises.push(handleReloadEvent(x.msg));
          promises.push(fetchEventsCount());
        }
        if (x.type === AI_COMMAND.MINUTLY_SUMMARY) {
          promises.push(handleMinutlySummary(x.msg));
        }
      });
      promises.push(handleFetchRrOverview());
      promises.push(handleReloadRrHeatMap());
      setInitTemplateOverview([]);
      await Promise.all(promises);
      removeChangeData();
    } catch (error) {
      logError('Error: ', error);
    } finally {
      updateDataMessageQueue.current.length = 0;
      emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: false });
      emitter.emit(EMITTER_CONSTANTS.RE_RENDER_ECG_VIEWER);
      emitter.emit(EMITTER_CONSTANTS.RELOAD_TEMPLATE_OVERVIEW);
    }
  };

  useEffect(() => {
    if (isStartedAiProcessing && isActiveTab) {
      handleFetchRrOverview();
    }
  }, [isStartedAiProcessing, isActiveTab]);

  // EMITTER FROM SOCKET
  useEmitter(
    EMITTER_CONSTANTS.EVENTSUPDATED_EVENT,
    async (msg) => {
      if (!isActiveTab) return;
      if (isPushQueue) {
        updateDataMessageQueue.current.push({
          type: AI_COMMAND.EVENT,
          msg: _.cloneDeep(msg),
        });
      } else {
        await handleReloadEvent(msg, true);
      }
    },
    [isActiveTab, isPushQueue],
  );

  useEmitter(
    EMITTER_CONSTANTS.RE_CAPTURE_AUTO_EVENTS,
    (msg) => {
      if (!isActiveTab) return;
      handleReloadEvent(msg, true);
    },
    [isActiveTab],
  );

  useEmitter(
    EMITTER_CONSTANTS.MINUTELY_HR_UPDATED,
    async (msg) => {
      if (isPushQueue) {
        updateDataMessageQueue.current.push({
          type: AI_COMMAND.MINUTLY_SUMMARY,
          msg,
        });
      } else {
        await handleMinutlySummary(msg);
      }
    },
    [isActiveTab, isPushQueue],
  );

  useEmitter(
    EMITTER_CONSTANTS.STOP_SAVING,
    async (msg: any) => {
      // execution time
      if (msg && isActiveTab) {
        if (msg?.data === 'done') {
          setIsPushQueue(false);
          console.time('Execution-time-saving');
          await handleSavingStatus();
          console.timeEnd('Execution-time-saving');
        }
      }
    },
    [isActiveTab],
  );
  return null;
};

export default BeatDataEffect;
